/**
 * 资料库「窄容器」布局判定 —— 纯逻辑，不依赖 React / DOM / 全局对象，便于单测。
 *
 * 背景：资料库此前是按**整屏宽度**设计的：卡片网格 + 一条从右侧滑入、宽 68% 的详情面板。
 * 现在它要作为创作页分屏工作台里的**窄面板**使用（用户拖拽分屏条决定宽度，
 * 常见 240–460px，也可能被拖得很宽），因此必须在两种布局间切换：
 *
 *   - 宽容器（>= 断点）：保持原样 —— 「卡片网格 + 右侧 68% 详情面板」两栏并存；
 *   - 窄容器（<  断点）：单栏「列表 ⇄ 详情」—— 只显示卡片列表，点某张卡片后详情铺满整栏，
 *     并给出「返回列表」按钮；返回后回到列表。
 *
 * 关键点：判定输入必须是**面板自身宽度**，不能用 `window.innerWidth`。
 * 分屏拖拽只改变面板宽度、改变不了视口宽度，用视口判断会得出与面板无关的错误结论
 * （面板被拖到 240px 时视口可能还是 1440px）。所以这里只吃一个「容器宽度」参数，
 * 由调用方用 `ResizeObserver` 量出面板根元素宽度后传进来。
 */

/**
 * 断点（px）：容器宽度**小于**该值时走单栏「列表 ⇄ 详情」。
 *
 * 取值理由 —— 现有两栏布局在两个硬约束下取更严的那个：
 *
 * 1) 左侧卡片条宽度。两栏时详情面板占容器 68%，左边露出的只有 32%；
 *    而 `card-list` 状态下卡片是**固定 227px** 宽（dataLibrary.css）。
 *    要不被详情面板压住，需要 `0.32 * W >= 227` → `W >= 710px`。
 * 2) 详情表单的可读宽度。详情面板内部还要吃掉 body 的 `20px * 2` 内边距；
 *    最紧的是人物详情的「年龄 / 实力」两列网格，每列不低于 ~150px 才不至于挤成一团，
 *    需要 `0.68 * W - 40 >= 300` → `W >= 500px`。
 *
 * 取更严的 710 并向上取整到 **720**：既覆盖上面两个约束，又与仓库里既有的窄屏断点
 * （`dataLibrary.css` 里的 `@media (max-width: 720px)`）对齐，避免出现两套「窄」的定义。
 *
 * 实际效果：240–460px 的常见窄面板一律判定为窄栏 —— 这正是本次改造的目标；
 * 只有把面板拖到 720px 以上的「很宽」档位，才回到原来的两栏体验。
 */
export const NARROW_BREAKPOINT_PX = 720;

export interface DataLayout {
  /** true = 单栏「列表 ⇄ 详情」；false = 两栏「卡片网格 + 右侧详情面板」 */
  narrow: boolean;
}

/**
 * 按容器宽度决定资料库布局。
 *
 * @param containerWidth 面板根元素的宽度（px，由 ResizeObserver 量出）
 */
export function resolveDataLayout(containerWidth: number): DataLayout {
  // 非有限值（首帧还没量、元素处在 display:none、上游算错得到 NaN）一律按窄栏处理：
  // 单栏布局在任何宽度下都能用，而两栏布局需要足够空间 —— 测不准时落到安全的那一侧，
  // 避免在 240px 的面板里先渲染一次「两栏」再跳回来（闪现）。
  if (!Number.isFinite(containerWidth)) return { narrow: true };
  return { narrow: containerWidth < NARROW_BREAKPOINT_PX };
}
